import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AppLayout from '../components/layout/AppLayout';
import type { Brand } from '../types/brand';
import {
    Tag,
    Beaker,
    Cylinder,
    MapPin,
    Edit3,
    Trash2,
    Plus,
    Info
} from 'lucide-react';

export default function Brands() {
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [brandType, setBrandType] = useState<'grower' | 'processor' | 'label'>('grower');
    const [location, setLocation] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        const { data, error } = await supabase
            .from('brands')
            .select('*')
            .order('name');

        if (error) {
            console.error('Error fetching brands:', error);
        } else if (data) {
            setBrands(data);
        }
        setLoading(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        if (editingId) {
            // Update existing brand
            const { error } = await supabase
                .from('brands')
                .update({
                    name,
                    brand_type: brandType,
                    location: location || null,
                    notes: notes || null,
                })
                .eq('id', editingId);

            if (error) {
                console.error('Error updating brand:', error);
                alert('Error updating brand.');
            } else {
                resetForm();
                fetchBrands();
            }
        } else {
            // Insert new brand
            const { error } = await supabase.from('brands').insert({
                name,
                brand_type: brandType,
                location: location || null,
                notes: notes || null,
            });

            if (error) {
                console.error('Error adding brand:', error);
                alert('Error adding brand. It may already exist.');
            } else {
                resetForm();
                fetchBrands();
            }
        }
        setSaving(false);
    };

    const handleEdit = (brand: Brand) => {
        setEditingId(brand.id);
        setName(brand.name);
        setBrandType(brand.brand_type);
        setLocation(brand.location || '');
        setNotes(brand.notes || '');
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this brand? This cannot be undone.')) {
            return;
        }

        const { error } = await supabase
            .from('brands')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting brand:', error);
            alert('Error deleting brand. It may be linked to existing products.');
        } else {
            fetchBrands();
        }
    };

    const resetForm = () => {
        setName('');
        setBrandType('grower');
        setLocation('');
        setNotes('');
        setShowForm(false);
        setEditingId(null);
    };

    const typeIcons = {
        grower: Tag,
        processor: Beaker,
        label: Cylinder,
    };

    const typeColors = {
        grower: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
        processor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        label: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    };

    if (loading) {
        return (
            <AppLayout title="Brands">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Tag className="text-accent-purple animate-pulse mb-4" size={48} />
                    <p className="text-dark-muted animate-pulse font-medium">Cataloging brands...</p>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout title="Brands">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight uppercase">Source Registry</h2>
                        <p className="text-[10px] text-dark-muted font-bold tracking-widest uppercase mt-1">Growers & Processors</p>
                    </div>
                    <button
                        onClick={() => {
                            if (showForm) resetForm();
                            else setShowForm(true);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${showForm ? 'bg-dark-surface border border-dark-border text-dark-muted' : 'bg-accent-purple text-white shadow-lg shadow-accent-purple/20 active:scale-95'
                            }`}
                    >
                        {showForm ? 'Cancel' : <><Plus size={14} /> New Brand</>}
                    </button>
                </div>

                {/* Add/Edit Form */}
                {showForm && (
                    <form onSubmit={handleSubmit} className="card bg-dark-surface/50 border-accent-purple/20 space-y-6 animate-in slide-in-from-top-4 duration-300">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-accent-purple/10 rounded-2xl border border-accent-purple/20">
                                <Plus size={20} className="text-accent-purple" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black tracking-tight">{editingId ? 'Modify Brand' : 'Register New Brand'}</h2>
                                <p className="text-[10px] text-dark-muted font-bold uppercase tracking-widest">Metadata Entry</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label htmlFor="name" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Brand name</label>
                                <input
                                    id="name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="input h-12 text-sm font-bold bg-dark-bg"
                                    placeholder="e.g. 710 Labs, Stiiizy"
                                    required
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="type" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Operation Type</label>
                                <select
                                    id="type"
                                    value={brandType}
                                    onChange={(e) => setBrandType(e.target.value as any)}
                                    className="input h-12 text-sm font-bold bg-dark-bg appearance-none cursor-pointer"
                                >
                                    <option value="grower">Grower (Cultivation)</option>
                                    <option value="processor">Processor (Extraction)</option>
                                    <option value="label">Label (Marketing Only)</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="location" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Origin / Location</label>
                            <div className="relative">
                                <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-muted" />
                                <input
                                    id="location"
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    className="input h-12 text-sm font-bold bg-dark-bg pl-12"
                                    placeholder="e.g. Los Angeles, CA"
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="notes" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Institutional Knowledge</label>
                            <textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="input min-h-[100px] resize-none text-sm font-medium bg-dark-bg p-4"
                                placeholder="Philosophy, specialty, or general repute..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary w-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-accent-purple/20 active:scale-95 transition-all"
                        >
                            {saving ? 'Processing...' : (editingId ? 'Commit Changes' : 'Initialize Brand')}
                        </button>
                    </form>
                )}

                {/* Brand List */}
                {brands.length === 0 ? (
                    <div className="card text-center py-24 bg-dark-surface/20 border-dashed border-dark-border group cursor-pointer hover:border-accent-purple/30 transition-all" onClick={() => setShowForm(true)}>
                        <div className="inline-flex p-6 rounded-3xl bg-dark-bg border border-dark-border mb-6 group-hover:scale-110 transition-transform">
                            <Tag size={48} className="text-accent-purple/30" />
                        </div>
                        <h2 className="text-xl font-bold mb-2">Registry Empty</h2>
                        <p className="text-sm text-dark-muted max-w-[240px] mx-auto leading-relaxed">
                            No sources detected. Brands help distinguish the same strain from different growers.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {brands.map((brand) => {
                            const Icon = typeIcons[brand.brand_type];
                            return (
                                <div key={brand.id} className="card group hover:border-accent-purple/30 transition-all relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none group-hover:opacity-10 transition-opacity">
                                        <Icon size={120} />
                                    </div>

                                    <div className="flex items-start justify-between mb-6 relative z-10">
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-2xl border ${typeColors[brand.brand_type]}`}>
                                                <Icon size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-black text-lg tracking-tight group-hover:text-white transition-colors">{brand.name}</h3>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${typeColors[brand.brand_type]}`}>
                                                        {brand.brand_type}
                                                    </span>
                                                    {brand.location && (
                                                        <span className="text-[9px] text-dark-muted font-bold flex items-center gap-1 uppercase tracking-tighter">
                                                            <MapPin size={10} className="text-accent-purple/40" /> {brand.location}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {brand.notes && (
                                        <div className="mb-6">
                                            <p className="text-xs text-dark-muted leading-relaxed font-medium line-clamp-2 italic">
                                                "{brand.notes}"
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex gap-2 pt-4 border-t border-dark-border/50 relative z-10">
                                        <button
                                            onClick={() => handleEdit(brand)}
                                            className="h-10 px-4 flex-1 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-purple/10 hover:border-accent-purple/30 text-dark-muted hover:text-accent-purple transition-all flex items-center justify-center gap-2"
                                        >
                                            <Edit3 size={12} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(brand.id)}
                                            className="h-10 px-4 flex-1 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-red/10 hover:border-accent-red/30 text-dark-muted hover:text-accent-red transition-all flex items-center justify-center gap-2"
                                        >
                                            <Trash2 size={12} /> Remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="card bg-dark-surface/30 border-dark-border/40 border-dashed">
                    <div className="flex gap-4">
                        <div className="shrink-0 p-2 bg-dark-bg rounded-lg border border-dark-border self-start">
                            <Info size={16} className="text-accent-purple/60" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-dark-muted uppercase tracking-widest mb-1">Architect's Note</p>
                            <p className="text-[11px] text-dark-muted/60 leading-relaxed font-medium">
                                Building a robust brand registry allows you to identify which growers consistently produce higher value scores for the same strain.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
